import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router";

import { ConfirmDialog } from "../../components/Dialog";
import {
  Alert,
  Badge,
  Button,
  Card,
  LoadingBlock,
  PageHeader,
  SelectField,
  TextArea,
  TextField,
} from "../../components/ui";
import { useToast } from "../../components/Toast";
import { api, errorMessage } from "../../lib/api";
import { useConfig } from "../../lib/config";
import { formatDate } from "../../lib/format";
import type { Message } from "../../lib/types";
import type { Membership } from "../orders/types";

interface Member {
  membership_id: string;
  user_id: string;
  name: string;
  email: string;
  role: "member" | "company_admin";
  status: "pending" | "approved" | "rejected" | "revoked";
  source: string;
  request_message: string | null;
  requested_at: string;
}

interface AdminView {
  company: { id: string; display_name: string; status: string };
  join_code: string | null;
  notification_emails: string[];
  members: Member[];
  invitations: {
    id: string;
    email: string;
    role: string;
    created_at: string;
    expires_at: string;
    status: string;
  }[];
}

const statusTone = {
  pending: "warn",
  approved: "ok",
  rejected: "danger",
  revoked: "neutral",
} as const;

function CompanyAdminPanel({ companyId }: { companyId: string }) {
  const qc = useQueryClient();
  const { notify } = useToast();
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const [confirm, setConfirm] = useState<Member | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [extraEmails, setExtraEmails] = useState<string | null>(null);
  const view = useQuery({
    queryKey: ["company-admin", companyId],
    queryFn: () => api.get<AdminView>(`/companies/${companyId}/admin`),
  });
  const refreshAll = () => qc.invalidateQueries({ queryKey: ["company-admin", companyId] });

  const act = async (fn: () => Promise<Message | unknown>, success: string) => {
    try {
      await fn();
      notify(success);
      await refreshAll();
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  if (view.isLoading) return <LoadingBlock />;
  if (!view.data) return <Alert tone="error">{errorMessage(view.error)}</Alert>;
  const v = view.data;
  const pending = v.members.filter((m) => m.status === "pending");
  const active = v.members.filter((m) => m.status === "approved");

  return (
    <div className="mt-4 flex flex-col gap-5 border-t border-line pt-4">
      <div>
        <h3 className="font-bold">Requests waiting for approval ({pending.length})</h3>
        <p className="text-sm text-ink-muted">
          Only approve people you know work with your company.
        </p>
        {pending.length === 0 ? (
          <p className="mt-2 text-sm">No requests right now.</p>
        ) : (
          <ul className="mt-2 divide-y divide-line">
            {pending.map((m) => (
              <li
                key={m.membership_id}
                className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <span>
                  <strong>{m.name}</strong> ({m.email}) · requested {formatDate(m.requested_at, tz)}
                  {m.request_message && (
                    <span className="block text-sm text-ink-muted">“{m.request_message}”</span>
                  )}
                </span>
                <span className="flex gap-2">
                  <Button
                    onClick={() =>
                      act(
                        () =>
                          api.post(
                            `/companies/${companyId}/members/${m.membership_id}/approve`,
                            {},
                          ),
                        "Request approved.",
                      )
                    }
                  >
                    Approve
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      act(
                        () =>
                          api.post(`/companies/${companyId}/members/${m.membership_id}/reject`, {}),
                        "Request declined.",
                      )
                    }
                  >
                    Decline
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h3 className="font-bold">Members ({active.length})</h3>
        <ul className="mt-2 divide-y divide-line">
          {active.map((m) => (
            <li
              key={m.membership_id}
              className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <span>
                <strong>{m.name}</strong> ({m.email}){" "}
                {m.role === "company_admin" && <Badge tone="brand">Company admin</Badge>}
              </span>
              <span className="flex flex-wrap gap-2">
                <Button
                  variant="ghost"
                  onClick={() =>
                    act(
                      () =>
                        api.put(`/companies/${companyId}/members/${m.membership_id}/role`, {
                          role: m.role === "company_admin" ? "member" : "company_admin",
                        }),
                      "Role updated.",
                    )
                  }
                >
                  {m.role === "company_admin" ? "Make member" : "Make company admin"}
                </Button>
                <Button variant="ghost" onClick={() => setConfirm(m)}>
                  Remove access
                </Button>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <h3 className="font-bold">Invite someone</h3>
          <form
            className="mt-2 flex flex-col gap-3"
            onSubmit={async (e) => {
              e.preventDefault();
              await act(
                () =>
                  api.post(`/companies/${companyId}/invitations`, {
                    email: inviteEmail,
                    role: inviteRole,
                  }),
                "Invitation sent.",
              );
              setInviteEmail("");
            }}
          >
            <TextField
              label="Email"
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <SelectField
              label="Role"
              required
              placeholder="Choose one"
              options={[
                { value: "member", label: "Member" },
                { value: "company_admin", label: "Company admin" },
              ]}
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
            />
            <Button type="submit" className="self-start">
              Send invitation
            </Button>
          </form>
          {v.invitations.length > 0 && (
            <ul className="mt-3 text-sm">
              {v.invitations.map((i) => (
                <li key={i.id} className="flex items-center justify-between gap-2 py-1">
                  <span>
                    {i.email} · {i.status}
                  </span>
                  {i.status === "pending" && (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        act(
                          () => api.del(`/companies/${companyId}/invitations/${i.id}`),
                          "Invitation cancelled.",
                        )
                      }
                    >
                      Cancel
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="font-bold">Join code</h3>
            <p className="text-sm text-ink-muted">
              Share this code privately with coworkers. They use it to <em>ask</em> to join; you
              still approve each person.
            </p>
            <p className="mt-2 font-mono text-xl tracking-widest">{v.join_code}</p>
            <Button
              variant="secondary"
              className="mt-2"
              onClick={() =>
                act(
                  () => api.post(`/companies/${companyId}/join-code/rotate`),
                  "New join code created.",
                )
              }
            >
              Create a new code
            </Button>
          </div>
          <div>
            <h3 className="font-bold">Extra email recipients</h3>
            <p className="text-sm text-ink-muted">
              Order emails go to the person who placed the order. Add addresses here (one per line)
              to copy them too.
            </p>
            <TextArea
              label="Addresses"
              rows={3}
              value={extraEmails ?? v.notification_emails.join("\n")}
              onChange={(e) => setExtraEmails(e.target.value)}
            />
            <Button
              variant="secondary"
              className="mt-2"
              onClick={() =>
                act(
                  () =>
                    api.put(`/companies/${companyId}/notification-emails`, {
                      emails: (extraEmails ?? "")
                        .split(/[\n,]/)
                        .map((s) => s.trim())
                        .filter(Boolean),
                    }),
                  "Saved.",
                )
              }
            >
              Save recipients
            </Button>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={!!confirm}
        title="Remove access?"
        body={
          <p>
            {confirm?.name} will no longer see this company's orders and files. You can invite them
            again later.
          </p>
        }
        confirmLabel="Remove access"
        danger
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const m = confirm!;
          setConfirm(null);
          await act(
            () => api.post(`/companies/${companyId}/members/${m.membership_id}/revoke`),
            "Access removed.",
          );
        }}
      />
    </div>
  );
}

export function CompanyPage() {
  const qc = useQueryClient();
  const { notify } = useToast();
  const [error, setError] = useState<string | null>(null);
  const mine = useQuery({
    queryKey: ["memberships"],
    queryFn: () => api.get<Membership[]>("/companies/mine"),
  });
  const joinForm = useForm<{ join_code: string; message: string }>();
  const newForm = useForm<{
    display_name: string;
    phone: string;
    street: string;
    city: string;
    state: string;
    zip_code: string;
  }>();
  const refresh = () => qc.invalidateQueries({ queryKey: ["memberships"] });

  if (mine.isLoading) return <LoadingBlock />;
  const live = (mine.data ?? []).filter((m) => m.status === "approved" || m.status === "pending");

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Company access"
        description="Shared company accounts let coworkers see and place orders for the company."
      />
      {error && <Alert tone="error">{error}</Alert>}
      <Card>
        <h2 className="text-lg font-bold">My companies</h2>
        {live.length === 0 ? (
          <p className="mt-2 text-ink-muted">
            You are not part of a company account. You can still place orders for yourself.
          </p>
        ) : (
          <ul className="mt-2 flex flex-col gap-4">
            {live.map((m) => (
              <li key={m.membership_id}>
                <p className="flex flex-wrap items-center gap-2">
                  <strong>{m.company.display_name}</strong>
                  <Badge tone={statusTone[m.status]}>
                    {m.status === "approved" ? "Approved" : "Waiting for approval"}
                  </Badge>
                  {m.company.status === "pending" && (
                    <Badge tone="warn">Bill's is verifying this company</Badge>
                  )}
                  {m.role === "company_admin" && m.status === "approved" && (
                    <Badge tone="brand">Company admin</Badge>
                  )}
                </p>
                {m.role === "company_admin" &&
                  m.status === "approved" &&
                  m.company.status === "active" && <CompanyAdminPanel companyId={m.company.id} />}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <h2 className="text-lg font-bold">Join a company</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Ask your company administrator for the join code. They must approve your request.
          </p>
          <form
            className="mt-3 flex flex-col gap-3"
            onSubmit={joinForm.handleSubmit(async (v) => {
              setError(null);
              try {
                const r = await api.post<Message>("/companies/join", v);
                notify(r.message);
                joinForm.reset();
                await refresh();
              } catch (err) {
                setError(errorMessage(err));
              }
            })}
          >
            <TextField
              label="Join code"
              required
              placeholder="ABCD-EFGH-JKLM"
              {...joinForm.register("join_code", { required: true })}
            />
            <TextField label="Message to the administrator" {...joinForm.register("message")} />
            <Button type="submit" className="self-start" busy={joinForm.formState.isSubmitting}>
              Send request
            </Button>
          </form>
        </Card>
        <Card>
          <h2 className="text-lg font-bold">Set up a new company account</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Bill's will verify the company before it can be used. You will be its company
            administrator.
          </p>
          <form
            className="mt-3 flex flex-col gap-3"
            onSubmit={newForm.handleSubmit(async (v) => {
              setError(null);
              try {
                await api.post(
                  "/companies",
                  Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x || null])),
                );
                notify("Thank you. Bill's will verify the company and email you.");
                newForm.reset();
                await refresh();
              } catch (err) {
                setError(errorMessage(err));
              }
            })}
          >
            <TextField
              label="Company name"
              required
              {...newForm.register("display_name", { required: true })}
            />
            <TextField label="Phone" type="tel" {...newForm.register("phone")} />
            <TextField label="Street address" {...newForm.register("street")} />
            <div className="grid grid-cols-3 gap-2">
              <TextField label="City" {...newForm.register("city")} />
              <TextField label="State" {...newForm.register("state")} />
              <TextField label="ZIP" {...newForm.register("zip_code")} />
            </div>
            <Button type="submit" className="self-start" busy={newForm.formState.isSubmitting}>
              Request company account
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const [token] = useState(() => params.get("token"));
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (token) window.history.replaceState(null, "", window.location.pathname);
  }, [token]);
  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api
      .post<Message>("/companies/invitations/accept", { token })
      .then((r) => setResult({ ok: true, message: r.message }))
      .catch((err) => setResult({ ok: false, message: errorMessage(err) }));
  }, [token]);
  return (
    <div className="mx-auto max-w-md">
      <h1 className="mb-4 text-2xl font-bold">Company invitation</h1>
      {!token && (
        <Alert tone="error">
          This link is missing information. Please use the link from your email.
        </Alert>
      )}
      {token && !result && <LoadingBlock label="Accepting invitation" />}
      {result && <Alert tone={result.ok ? "success" : "error"}>{result.message}</Alert>}
      <Link to="/orders" className="mt-4 inline-block text-brand-700 underline">
        Go to my orders
      </Link>
    </div>
  );
}
